import React from 'react';

import StoryScroll, { StorySection } from '../../../components/blocks/StoryScroll';

// Same 4 pillars as the original static cards, presented as a pinned
// scroll-stack. Each section is full-screen; the next one rotates up from
// the bottom-left corner as you scroll. Sections share a consistent layout
// (number tag → divider → giant headline → divider → supporting paragraph)
// but alternate light/dark backgrounds across the brand palette.

const PILLARS = [
  {
    eyebrow: '01 — Direct supply',
    headline: ['No', 'middle', 'men'],
    body:
      'FPOs list to bulk buyers without commission agents. More of every rupee paid stays with the farmer instead of evaporating across a 4-hop chain.',
    bg: '#052e16', // green-950
    fg: '#ffffff',
    rule: 'rgba(255,255,255,0.35)',
    accent: 'rgba(187,247,208,0.9)', // green-200
  },
  {
    eyebrow: '02 — Transparent pricing',
    headline: ['Live', 'unit', 'rates'],
    body:
      'Every listing carries a clear price per kg / quintal / tonne and a published minimum-order threshold. No back-and-forth, no surprise invoices.',
    bg: '#fdf8f0', // earth-50 (warm cream)
    fg: '#052e16',
    rule: 'rgba(5,46,22,0.25)',
    accent: '#166534', // green-800
    pillars: [
      { k: 'Per-unit', v: 'kg · quintal · tonne — your choice.' },
      { k: 'Min-order', v: 'Buyers see the floor before they ever click add to cart.' },
      { k: 'Snapshot', v: "Title + unit price freeze the moment an order is placed." },
    ],
  },
  {
    eyebrow: '03 — Aggregated stock',
    headline: ['One', 'FPO,', 'every', 'farm'],
    body:
      "Buyers want one invoice for 50 tonnes of tomatoes — not 50 separate growers chasing fifty payments. The FPO pools its members' produce into a single listing.",
    bg: '#ffffff',
    fg: '#052e16',
    rule: 'rgba(5,46,22,0.15)',
    accent: '#15803d', // green-700
    pillars: [
      { k: 'Pooled', v: 'Member farms roll up into one aggregated quantity.' },
      { k: 'Atomic', v: 'Stock decrements automatically as orders land — no double-selling.' },
      { k: 'Archived', v: 'Auto-archives when quantity hits zero.' },
    ],
  },
  {
    eyebrow: '04 — Traceable to farms',
    headline: ['Provenance', 'built', 'in'],
    body:
      'Each listing can link to the member farms it was sourced from — the buyer sees village, soil type, and primary crop on the farms behind the produce. Trust isn\'t a marketing slogan; it\'s a foreign-key chain.',
    bg: '#15803d', // green-700
    fg: '#ffffff',
    rule: 'rgba(255,255,255,0.40)',
    accent: 'rgba(187,247,208,0.95)',
  },
];

function PillarChips({ pillars, rule }) {
  if (!pillars) return null;
  return (
    <>
      <hr className="my-[2vw] border-none border-t" style={{ borderTopColor: rule }} />
      <div className="flex flex-wrap gap-[3vw]">
        {pillars.map((p) => (
          <div key={p.k} className="min-w-[180px] flex-1">
            <p className="mb-2 text-sm font-bold uppercase tracking-wider">{p.k}</p>
            <p className="text-[clamp(0.85rem,1.3vw,1.05rem)] leading-relaxed opacity-80">{p.v}</p>
          </div>
        ))}
      </div>
    </>
  );
}

export default function WhyAtharvaSection() {
  return (
    <section id="why">
      <StoryScroll ariaLabel="Why Atharva — the four pillars">
        {PILLARS.map((p) => (
          <StorySection
            key={p.eyebrow}
            ariaLabel={p.eyebrow}
            style={{ backgroundColor: p.bg, color: p.fg }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: p.accent }}>
              {p.eyebrow}
            </p>
            <hr className="my-[2vw] border-none border-t" style={{ borderTopColor: p.rule }} />
            <div>
              <h2 className="text-[clamp(3.5rem,12vw,14rem)] font-bold leading-[0.85] uppercase tracking-tight">
                {p.headline.map((line, i) => (
                  <React.Fragment key={i}>
                    {line}
                    {i < p.headline.length - 1 && <br />}
                  </React.Fragment>
                ))}
              </h2>
            </div>
            <hr className="my-[2vw] border-none border-t" style={{ borderTopColor: p.rule }} />
            <p className="max-w-[55ch] text-[clamp(1rem,2.2vw,1.75rem)] font-normal leading-relaxed">
              {p.body}
            </p>
            <PillarChips pillars={p.pillars} rule={p.rule} />
          </StorySection>
        ))}
      </StoryScroll>
    </section>
  );
}
